import { TFunction } from "i18next";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import style from "./user-profile.scss";
import CopyToClipboard from "components/copy-to-clipboard/CopyToClipboard";
import { SubpageLayout } from "components/layout/subpage-layout/SubpageLayout";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import { PROFILE_ROUTE } from "components/router/Routes";
import Heading from "components/typography/heading/Heading";
import { UserDetails } from "domain/user";
import { getPublicApiUrl } from "services/login/endpointRepository";
import { createDateLabel, formatTimestamp } from "utils/format";

import testIds from "testIds.json";

function createUserObject(label: string, testId: string, value?: string, style?: string) {
    return {
        label,
        testId,
        value,
        style,
    };
}

interface Props {
    user: UserDetails | null;
}

function createUserObjects(t: TFunction, props: Props) {
    // As of now the compiler doesn't understand that this is impossible because
    // this check is already performed earlier and this method is never called
    // if this is null. Still, compilation fails without this check so it must
    // be here.
    if (props.user === null) {
        return [];
    }
    const baseObjects = [
        createUserObject(
            t("UserProfile.username"),
            testIds.navigation.userProfileDialog.user.usernameLabel,
            props.user.username
        ),
        createUserObject(
            t("Common.nameWithColon"),
            testIds.navigation.userProfileDialog.user.nameLabel,
            props.user.name !== "" ? props.user.name : t("Common.notProvided"),
            props.user.name === "" ? style.emptyField : ""
        ),
        createUserObject(
            t("Common.emailWithColon"),
            testIds.navigation.userProfileDialog.user.emailLabel,
            props.user.email
        ),
        createUserObject(
            t("UserProfile.userIdentifier"),
            testIds.navigation.userProfileDialog.user.uuidLabel,
            props.user.uuid
        ),
        createUserObject(
            t("UserProfile.created"),
            testIds.navigation.userProfileDialog.user.createdLabel,
            createDateLabel(props.user.created)
        ),
        createUserObject(
            t("UserProfile.modified"),
            testIds.navigation.userProfileDialog.user.modifiedLabel,
            createDateLabel(props.user.modified)
        ),
    ];
    baseObjects.push(
        createUserObject(
            t("UserProfile.lastLogin"),
            testIds.navigation.userProfileDialog.user.lastLoginLabel,
            props.user.lastLogin ? formatTimestamp(props.user.lastLogin) : t("Common.na")
        ),
        createUserObject(
            t("UserProfile.loginCount"),
            testIds.navigation.userProfileDialog.user.loginCountLabel,
            props.user.totalLogins ? props.user.totalLogins.toString() : t("Common.na")
        )
    );
    return baseObjects;
}

export const UserProfile = (props: Props): JSX.Element => {
    if (props.user === null) {
        return <LoadingIndicator />;
    }

    const { t } = useTranslation();
    const history = useHistory();

    const handleClose = React.useCallback(() => {
        if (history.length > 1) {
            history.goBack();
        } else {
            history.push("/");
        }
    }, [history]);

    const userFragments = createUserObjects(t, props).map((each, index) => (
        <div key={index} className={style.userProfileRow}>
            <div className={style.label}>{each.label}</div>
            <div className={each.style} data-testid={each.testId}>
                {each.value}
                {each.label == t("UserProfile.username") ||
                each.label == t("Common.emailWithColon") ||
                each.label == t("UserProfile.userIdentifier")
                    ? each.value != undefined && <CopyToClipboard value={each.value} />
                    : ""}
            </div>
        </div>
    ));
    return (
        <SubpageLayout visible={true} title={t(PROFILE_ROUTE.title)} primaryButtonRequired={true} onClose={handleClose}>
            <div className={style.userProfileWrapper}>
                <Heading tag={"div"} variant="SUBTITLE_1">
                    {t("UserProfile.generalDetails")}
                </Heading>
                {userFragments}
                <Heading tag={"div"} variant="SUBTITLE_1">
                    {t("UserProfile.customerDetails")}
                </Heading>

                <div className={style.userProfileRow}>
                    <div className={style.label}>{t("UserProfile.customerName")}</div>
                    <div data-testid={testIds.navigation.userProfileDialog.tenant.nameLabel}>
                        {props.user.tenantName}
                    </div>
                </div>

                <div className={style.userProfileRow}>
                    <div className={style.label}>{t("UserProfile.customerIdentifier")}</div>
                    <div data-testid={testIds.navigation.userProfileDialog.tenant.uuidLabel}>
                        {props.user.tenantUuid}
                        {props.user?.erasureClientEndpoint != null && <CopyToClipboard value={props.user.tenantUuid} />}
                    </div>
                </div>
                <div className={style.userProfileRow}>
                    <div className={style.label}>{t("UserProfile.statusActive")}</div>
                    <div data-testid={testIds.navigation.userProfileDialog.tenant.activeLabel}>
                        {props.user.tenantStatus
                            ? t("Common.yes")
                            : props.user.tenantStatus === false
                              ? t("Common.no")
                              : "-"}
                    </div>
                </div>

                <div className={style.userProfileRow}>
                    <div className={style.label}>{t("Common.accessExpiry")}:</div>
                    <div data-testid={testIds.navigation.userProfileDialog.tenant.accessExpirationLabel}>
                        {props.user.tenantExpirationDate
                            ? createDateLabel(props.user.tenantExpirationDate)
                            : t("Common.never")}
                    </div>
                </div>

                <Heading tag={"div"} variant="SUBTITLE_1">
                    {t("UserProfile.erasureClientSettings")}
                </Heading>

                <div className={style.userProfileRow}>
                    <div className={style.label}>{t("Common.hostname")}</div>
                    <div data-testid={testIds.navigation.userProfileDialog.erasureClient.endpoint}>
                        {props.user.erasureClientEndpoint}
                        {props.user?.erasureClientEndpoint != null && (
                            <CopyToClipboard value={props.user.erasureClientEndpoint} />
                        )}
                    </div>
                </div>

                <div className={style.userProfileRow}>
                    <div className={style.label}>{t("Common.port")}</div>
                    <div data-testid={testIds.navigation.userProfileDialog.erasureClient.port}>443</div>
                </div>

                <Heading tag={"div"} variant="SUBTITLE_1">
                    {t("UserProfile.publicApiSettings")}
                </Heading>

                <div className={style.userProfileRow}>
                    <div className={style.label}>{t("Common.hostname")}</div>
                    <div data-testid={testIds.navigation.userProfileDialog.publicApi.endpointLabel}>
                        {getPublicApiUrl()}
                        <CopyToClipboard value={getPublicApiUrl()} />
                    </div>
                </div>

                <div className={style.userProfileRow}>
                    <div className={style.label}>{t("Common.port")}</div>
                    <div data-testid={testIds.navigation.userProfileDialog.publicApi.portLabel}>443</div>
                </div>

                <Heading tag={"div"} variant="SUBTITLE_1">
                    {t("UserProfile.statusSettings")}
                </Heading>

                <div className={style.userProfileRow}>
                    <div className={style.label}>{t("UserProfile.statusActive")}</div>
                    <div data-testid={testIds.navigation.userProfileDialog.status.activeLabel}>
                        {props.user.enabled ? t("Common.yes") : props.user.enabled === false ? t("Common.no") : "-"}
                    </div>
                </div>

                <div className={style.userProfileRow}>
                    <div className={style.label}>{t("Common.accessExpiry")}:</div>
                    <div data-testid={testIds.navigation.userProfileDialog.status.accessExpirationLabel}>
                        {props.user.userExpirationDate
                            ? createDateLabel(props.user.userExpirationDate)
                            : t("Common.never")}
                    </div>
                </div>
            </div>
        </SubpageLayout>
    );
};
